import React, { useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Users } from "../api/models/user.models";
import { Company } from "../api/models/companies.models";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { useRolesForSelect } from "../utils/roleUtils";
import usePermissions from "../hooks/usePermissions";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { Checkbox } from "primereact/checkbox";
import useAllOrganizationPermissions from "../hooks/useAllOrganizationPermissions";
import { useUserContext } from "../contexts/userContext";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .form-container {
    padding: 0 2rem;
    flex: 1;
    display: flex;
    flex-direction: column;

    & .p-button {
      max-width: 200px;
      margin-left: auto;
    }
  }

  .p-multiselect-token-label {
    font-size: 0.875rem;
  }

  .organization-dropdown-container {
    margin: 4rem 2rem 1rem auto;
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 2rem;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 1;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

const GridContainer = styled.div`
  display: grid;
  margin-top: 1rem;
  grid-template-columns: minmax(400px, 1fr) repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;

  & label {
    text-align: center;
  }
  & .p-multiselect {
    width: 350px;
  }
`;

const CompanyNameLabel = styled.label`
  margin-bottom: auto;
  font-weight: 600;
  font-size: 1rem;
`;

const CompanyAndOrganizationContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left !important;

  & label {
    text-align: left !important;
  }

  & .company-name {
    font-weight: 600;
    font-size: 1rem;
  }

  & .organization-name {
    font-size: 1rem;
    padding-left: 1rem;
  }
`;

interface EditUserRolesDialogProps {
  show: boolean;
  onHide: () => void;
  user: Users;
  companies: Company[];
  companyId?: number; // Optional companyId to filter permissions
  organizationId: number | null;
}

const EditUserRolesDialog: React.FC<EditUserRolesDialogProps> = ({
  show,
  onHide,
  user,
  companies,
  companyId,
  organizationId,
}) => {
  const { t } = useTranslation();
  const companyMenu = useRef<Menu>(null);
  const [selectedCompaniesAndRoles, setSelectedCompaniesAndRoles] =
    React.useState<
      { companyId: number; organizationId: number | null; roleIds: number[] }[]
    >([]);

  const roleOptions = useRolesForSelect();

  const { user: selfUser } = useUserContext();

  const allCompanyOrganizationIds =
    companies
      .find((c) => c.id === (companyId as number))
      ?.organizations.map((org) => org.id)
      .filter((id) => id !== undefined) || [];

  const {
    fetchAllPermissions,
    grantPermissions,
    isGrantingPermissions,
    error,
    permissionsData,
    permissionsIsLoading,
    revokePermissions,
    isRevokingPermissions,
  } = useAllOrganizationPermissions({
    userId: user.id,
    companyId: companyId,
    organizationIds: allCompanyOrganizationIds,
  });

  useEffect(() => {
    fetchAllPermissions();
  }, []);

  React.useEffect(() => {
    if (
      permissionsData &&
      permissionsData.length > 0 &&
      selectedCompaniesAndRoles.length === 0
    ) {
      const grouped: {
        companyId: number;
        organizationId: number | null;
        roleIds: number[];
      }[] = [];

      const mainLevelPermissions = permissionsData.filter(
        (permission) => !permission.organizationId
      );

      // Collect all main-level permission IDs
      const mainLevelPermissionIds = mainLevelPermissions.map((p) => p.id);

      if (mainLevelPermissions.length > 0) {
        grouped.push({
          companyId: mainLevelPermissions[0].companyId as number,
          organizationId: null,
          roleIds: mainLevelPermissionIds,
        });
      }

      const orgPermissions = permissionsData.filter(
        (permission) => permission.organizationId !== null
      );

      orgPermissions.forEach((permission) => {
        // Skip if this permission ID already exists in main-level permissions
        if (mainLevelPermissionIds.includes(permission.id)) {
          return;
        }

        const normalizedOrgId = permission.organizationId ?? null;
        const existingGroup = grouped.find(
          (g) =>
            g.companyId === (permission.companyId as number) &&
            g.organizationId === normalizedOrgId
        );
        if (existingGroup) {
          existingGroup.roleIds.push(permission.id);
        } else {
          grouped.push({
            companyId: permission.companyId as number,
            organizationId: normalizedOrgId,
            roleIds: [permission.id],
          });
        }
      });

      setSelectedCompaniesAndRoles(grouped);
    }
  }, [permissionsData]);

  const onCompanyRoleChange = (
    e: any,
    companyId: number,
    organizationId: number | null
  ) => {
    const updated = selectedCompaniesAndRoles.map((cr) => {
      if (cr.companyId === companyId && cr.organizationId === organizationId) {
        return { ...cr, roleIds: e.value };
      }
      return cr;
    });
    setSelectedCompaniesAndRoles(updated);
  };

  const handleRemovePermission = (
    companyId: number,
    organizationId: number | null,
    roleId: number
  ) => {
    const updated = selectedCompaniesAndRoles.map((cr) => {
      if (cr.companyId === companyId && cr.organizationId === organizationId) {
        return {
          ...cr,
          roleIds: cr.roleIds.filter((id) => id !== roleId),
        };
      }
      return cr;
    });
    revokePermissions({
      userId: user.id,
      companyId: companyId,
      organizationId: organizationId || undefined,
      permissions: [roleId],
    });
    setSelectedCompaniesAndRoles(updated);
  };

  const getCompanyMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = companies
      .filter(
        (c) => !selectedCompaniesAndRoles.find((sc) => sc.companyId === c.id)
      )
      .map((company) => ({
        label: company.companyName,
        command: () => {
          setSelectedCompaniesAndRoles((prev) => [
            ...prev,
            { companyId: company.id, organizationId: null, roleIds: [] },
          ]);
        },
      }));
    return items;
  };

  const getOrganizationName = (companyAndRole: {
    companyId: number;
    organizationId: number | null;
    roleIds: number[];
  }) => {
    const organizationName = companies
      .find((c) => c.id === companyAndRole.companyId)
      ?.organizations?.find(
        (or) => or.id === companyAndRole.organizationId
      )?.organizationName;
    return organizationName;
  };

  // Helper function to check if a permission exists at company level (main level)
  const isPermissionGrantedAtCompanyLevel = (
    companyId: number,
    roleId: number
  ): boolean => {
    const companyLevelPermissions = selectedCompaniesAndRoles.find(
      (cr) => cr.companyId === companyId && cr.organizationId === null
    );
    return companyLevelPermissions?.roleIds.includes(roleId) || false;
  };

  const selectedCompany = companies.find((c) => c.id === companyId);

  const organizationMenu = useRef<Menu>(null);

  const getOrganizationMenuItems = (): MenuItem[] => {
    if (!selectedCompany) return [];

    // Get organization IDs that user already has roles in
    const existingOrganizationIds = selectedCompaniesAndRoles
      .filter(
        (sc) =>
          sc.companyId === selectedCompany.id && sc.organizationId !== null
      )
      .map((sc) => sc.organizationId);

    // Filter out organizations that user already has roles in
    const availableOrganizations =
      selectedCompany.organizations?.filter(
        (org) => !existingOrganizationIds.includes(org.id as number)
      ) || [];

    return availableOrganizations.map((organization) => ({
      label: organization.organizationName,
      command: () => {
        setSelectedCompaniesAndRoles((prev) => [
          ...prev,
          {
            companyId: selectedCompany.id,
            organizationId: organization.id as number,
            roleIds: [],
          },
        ]);
      },
    }));
  };

  return (
    <StyledDialog
      header={t("label-select-user-access-and-roles")}
      visible={show}
      style={{ minWidth: "1200px", minHeight: "300px" }}
      onHide={onHide}
    >
      <LoadingContainer
        status={
          permissionsIsLoading
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <div className="form-container">
          <GridContainer>
            <CompanyNameLabel></CompanyNameLabel>
            {roleOptions.map((role) => (
              <label
                key={role.value}
                style={{
                  fontWeight: 500,
                  fontSize: "1rem",
                }}
              >
                {role.label}
              </label>
            ))}
          </GridContainer>

          {selectedCompaniesAndRoles.map((companyAndRole, index) => {
            const isCompanyAdmin = companyAndRole.roleIds.includes(900);
            const isReportCreator = companyAndRole.roleIds.includes(400);
            const organizationName = getOrganizationName(companyAndRole);
            const isOrganizationLevel = companyAndRole.organizationId !== null;

            return (
              <>
                <GridContainer key={companyAndRole.companyId}>
                  <CompanyAndOrganizationContainer>
                    <CompanyNameLabel
                      htmlFor={`company-${companyAndRole.companyId}`}
                    >
                      {
                        companies.find((c) => c.id === companyAndRole.companyId)
                          ?.companyName
                      }
                    </CompanyNameLabel>
                    {organizationName && (
                      <span className="organization-name">
                        {`- `}
                        {organizationName}
                      </span>
                    )}
                  </CompanyAndOrganizationContainer>

                  {roleOptions.map((role) => {
                    const hasCompanyLevelPermission =
                      isOrganizationLevel &&
                      isPermissionGrantedAtCompanyLevel(
                        companyAndRole.companyId,
                        role.value as number
                      );

                    const isSelfUserAdmin =
                      selfUser?.id === user.id &&
                      role.value === 900 &&
                      companyAndRole.roleIds.includes(900);

                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "nowrap",
                          gap: "0.6rem",
                          justifyContent: "center",
                        }}
                        key={role.value}
                      >
                        <Checkbox
                          id={`company-${companyAndRole.companyId}-role-${role.value}`}
                          checked={
                            companyAndRole.roleIds.includes(
                              role.value as number
                            ) ||
                            isCompanyAdmin ||
                            (isReportCreator && role.value === 300) ||
                            hasCompanyLevelPermission
                          }
                          onChange={(e) => {
                            if (e.checked) {
                              // Add role
                              onCompanyRoleChange(
                                {
                                  value: [
                                    ...companyAndRole.roleIds,
                                    role.value as number,
                                  ],
                                },
                                companyAndRole.companyId,
                                companyAndRole.organizationId
                              );
                              grantPermissions({
                                userId: user.id,
                                companyId: companyAndRole.companyId,
                                organizationId:
                                  companyAndRole.organizationId || undefined,
                                permissions: [role.value as number],
                              });
                            } else {
                              // Remove role
                              handleRemovePermission(
                                companyAndRole.companyId,
                                companyAndRole.organizationId,
                                role.value as number
                              );
                            }
                          }}
                          disabled={
                            (isCompanyAdmin && role.value !== 900) ||
                            (isReportCreator && role.value === 300) ||
                            hasCompanyLevelPermission ||
                            isSelfUserAdmin
                          }
                        />
                      </div>
                    );
                  })}
                </GridContainer>
              </>
            );
          })}
        </div>

        <div
          style={{
            margin: "5rem 2rem 1rem auto",
            display: "flex",
            gap: "1rem",
          }}
        >
          <Button
            label={t("cmd-add-organization-rights")}
            icon="pi pi-plus"
            onClick={(e) => organizationMenu.current?.toggle(e)}
            disabled={getOrganizationMenuItems().length === 0}
          />
          <Menu
            model={getOrganizationMenuItems()}
            popup
            ref={organizationMenu}
            id="organization-popup-menu"
          />
        </div>

        <Menu
          model={getCompanyMenuItems()}
          popup
          ref={companyMenu}
          id="companies-popup-menu"
        />

        <DialogActions>
          <Button
            type="button"
            label={t("cmd-close")}
            className="p-button-text"
            onClick={onHide}
          />
        </DialogActions>
      </LoadingContainer>
    </StyledDialog>
  );
};

export default EditUserRolesDialog;
